// Desktop notification when an operator submits a task.
//
// Deliberately its own listener rather than a hook into useTaskCache: that
// query carries whatever filters and page the user currently has on, so it
// re-subscribes when they change a filter and re-delivers every document as
// "added". Notifications must fire for any new task regardless of the filter
// bar, and must never fire because someone paged the list.
//
// Off by default, and it cannot be otherwise: browsers only grant notification
// permission from a user gesture, so the toggle is the gesture. When it is off
// no listener is opened at all — this costs nothing until it is asked for.
//
// The preference lives in localStorage keyed by workspace, matching the
// permission it depends on: notification permission is granted per browser,
// not per account, so a server-side setting would promise something it cannot
// keep on a different machine.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getFirestore, collection, query, where, orderBy, limit,
} from 'firebase/firestore';
import app from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import useClaimsReadySnapshot from './useClaimsReadySnapshot';
import { taskTitle } from '../utils/taskTitle';

export const NOTIFY_PREF_KEY = (subscriberId) =>
  `traklog:notifyNewTasks:${subscriberId || 'none'}`;

/** How many recent tasks to watch. Only ever used to spot additions. */
const WATCH_LIMIT = 5;

export function notificationsSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function readPref(subscriberId) {
  try {
    return window.localStorage.getItem(NOTIFY_PREF_KEY(subscriberId)) === 'true';
  } catch {
    // Private browsing and locked-down profiles throw on access.
    return false;
  }
}

function writePref(subscriberId, value) {
  try {
    window.localStorage.setItem(NOTIFY_PREF_KEY(subscriberId), String(value));
  } catch {
    /* preference simply will not persist */
  }
}

/** Wording that tells a supervisor whether they need to act, at a glance. */
export function describeTask(task) {
  const id = taskTitle(task);
  const who = task.operatorName || 'An operator';
  const template = task.templateName || 'a task';
  const customer = task.customerName ? ` for ${task.customerName}` : '';
  return {
    title: `${template}: ${id}`,
    body: `${who} submitted${customer}.`,
  };
}

export default function useNewTaskNotifications() {
  const { subscriberId } = useAuth();
  const location = useLocation();
  const supported = notificationsSupported();

  const [enabled, setEnabledState] = useState(() =>
    (supported && subscriberId ? readPref(subscriberId) : false));
  const [permission, setPermission] = useState(() =>
    (supported ? Notification.permission : 'denied'));

  // Re-read when the workspace changes: the preference is per workspace.
  useEffect(() => {
    setEnabledState(supported && subscriberId ? readPref(subscriberId) : false);
  }, [subscriberId, supported]);

  // The list in front of them already updates live, so a notification for a
  // task they can see is just noise. Anywhere else — another page, another
  // tab, another window — it is the whole point.
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  const shouldNotify = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return true;
    }
    return locationRef.current !== '/tasks';
  }, []);

  const setEnabled = useCallback(async (next) => {
    if (!supported) return false;
    if (!next) {
      setEnabledState(false);
      writePref(subscriberId, false);
      return false;
    }
    // Requesting is only allowed from a user gesture, which is why this is
    // called from the toggle and never on mount.
    let result = Notification.permission;
    if (result === 'default') {
      try {
        result = await Notification.requestPermission();
      } catch {
        result = 'denied';
      }
    }
    setPermission(result);
    const granted = result === 'granted';
    setEnabledState(granted);
    writePref(subscriberId, granted);
    return granted;
  }, [subscriberId, supported]);

  // One listen's own memory: what the first snapshot (the backlog) held, and
  // when the listen began. Started afresh with every listen, which
  // makeTarget below is called once for.
  const listenRef = useRef({ primed: false, seen: new Set(), openedAt: 0 });

  // The app bar mounts this before a workspace is chosen, so the listener
  // waits for the token to carry the workspace (useClaimsReadySnapshot).
  useClaimsReadySnapshot(
    (sid) => {
      if (!supported || !enabled || permission !== 'granted') return null;
      // The first snapshot is the backlog, not news. Without this every
      // enable fires five notifications for tasks the user has already seen.
      listenRef.current = { primed: false, seen: new Set(), openedAt: Date.now() };
      return query(
        collection(getFirestore(app), 'tasks'),
        where('subscriber_id', '==', sid),
        where('deleted', '==', false),
        orderBy('submittedAt', 'desc'),
        limit(WATCH_LIMIT),
      );
    },
    {
      subscriberId: subscriberId || null,
      label: 'New-task notifications',
      onNext: (snapshot) => {
        const listen = listenRef.current;
        if (!listen.primed) {
          snapshot.docs.forEach(d => listen.seen.add(d.id));
          listen.primed = true;
          return;
        }
        snapshot.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          if (change.doc.metadata.hasPendingWrites) return;
          if (listen.seen.has(change.doc.id)) return;
          listen.seen.add(change.doc.id);

          // Guards against a re-subscribe replaying older documents as added.
          const task = change.doc.data();
          const submitted = task.submittedAt?.toDate?.()?.getTime?.();
          if (submitted && submitted < listen.openedAt) return;

          if (!shouldNotify()) return;

          const { title, body } = describeTask(task);
          try {
            const note = new Notification(title, {
              body,
              tag: `traklog-task-${change.doc.id}`,   // collapse duplicates
              // Not favicon.ico: at most 64px, so the notification scaled it
              // up to a blur. 192px is the size Chrome renders it at.
              icon: '/icon-192.png',
            });
            note.onclick = () => {
              window.focus();
              window.location.assign(`/tasks/${change.doc.id}`);
              note.close();
            };
          } catch {
            /* a browser that refuses the constructor must not break the app */
          }
        });
      },
      onError: (error) => {
        // Losing notifications must never take the page down with it.
        console.warn('New-task notifications unavailable:', error?.message);
      },
    },
    [Boolean(supported), Boolean(enabled), permission],
  );

  return {
    supported,
    permission,
    enabled,
    // Permission is per browser and only the user can restore it once denied.
    blocked: supported && permission === 'denied',
    setEnabled,
  };
}
